import type { ActivityOverviewResponse, ActivityOverviewStatusFilter } from '@sparkapp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CatalogueHealthStrip } from './CatalogueHealthStrip';
import { PreferenceDomainHeatmap } from './PreferenceDomainHeatmap';
import { DimensionBreakdown } from './DimensionBreakdown';
import { activityListHref } from './activityListHref';

interface AllDomainsViewProps {
  data: ActivityOverviewResponse;
  status: ActivityOverviewStatusFilter;
  onSelectDomain?: (key: string) => void;
}

/** Catalogue-wide view: health strip, Preference × Domain heatmap, pack and play-type breakdowns. */
export function AllDomainsView({ data, status, onSelectDomain }: AllDomainsViewProps) {
  return (
    <div className="space-y-6">
      <CatalogueHealthStrip health={data.health} noActiveDomainCount={data.noActiveDomainCount} />

      <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
        <CardHeader>
          <CardTitle className="text-heading-4 text-pine-ink">Preference × Domain</CardTitle>
        </CardHeader>
        <CardContent>
          <PreferenceDomainHeatmap
            status={status}
            preferences={data.preferences}
            domains={data.domains}
            onSelectDomain={onSelectDomain}
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <DimensionBreakdown
          title="By Pack"
          note="An activity can be in more than one pack, so counts can add up to more than the total."
          rows={data.packs}
          missingCount={data.health.noPack}
          totalActivities={data.totalActivities}
          missingLabel="not in any pack"
          hrefFor={(id) => activityListHref(status, { physicalDeckId: id })}
        />
        <DimensionBreakdown
          title="By Play Type"
          note="An activity can have more than one play type, so counts can add up to more than the total."
          rows={data.playTypes}
          missingCount={data.health.noPlayType}
          totalActivities={data.totalActivities}
          missingLabel="with no play type"
          hrefFor={(id) => activityListHref(status, { playTypeId: id })}
        />
      </div>
    </div>
  );
}
